import { useEffect } from 'react';
import {
  useGetAgencyInformationForUser,
  useInvalidateUseGetAgencyInformationForUser,
} from '@urbanx/agx.agencies.client';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  useFirstPaintLoader,
} from '@urbanx/agx.ui-components2';
import { useFetchAvailableForms } from 'features/forms/formsReducer';
import { useAppSelector } from 'hooks/useAppSelector';
import { Navigate, Outlet } from 'react-router-dom';
import { LoadingState } from 'utils/loadingState';

import './RequireIntegrationAccessLayout.scss';

const RequireIntegrationAccessLayout = () => {
  const { data: agencyInformation, isPending } = useGetAgencyInformationForUser();
  const invalidateAgencyInformation = useInvalidateUseGetAgencyInformationForUser();
  const fetchAvailableForms = useFetchAvailableForms();
  const { availableForms, loadingState: formsLoadingState } = useAppSelector(
    (state) => state.forms
  );
  const formsPending =
    formsLoadingState === LoadingState.NotLoaded || formsLoadingState === LoadingState.Loading;

  useEffect(() => {
    void fetchAvailableForms();
  }, []);

  useFirstPaintLoader({ isInitialLoadComplete: !isPending && !formsPending });

  if (isPending || formsPending) return null;

  if (!agencyInformation) {
    return (
      <AgxSpacer
        direction="vertical"
        gap="spacing-lg"
        padding="spacing-3xl"
        contentAlignment="center"
        contentJustification="center"
        className="integration-access-error"
      >
        <AgxTypography
          id="agency-information-error"
          type="body_ui"
          size="medium"
          color="var(--text-tertiary, #94a3b8)"
        >
          Unable to load your agency information.
        </AgxTypography>
        <AgxButton
          id="retry-agency-information"
          text="Try again"
          variant="primary"
          onClick={() => void invalidateAgencyInformation()}
        />
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          contentAlignment="center"
          contentJustification="center"
          wrap
        >
          <AgxTypography
            id="agency-information-support-label"
            type="body_ui"
            size="medium"
            color="var(--text-tertiary, #94a3b8)"
          >
            Need help?
          </AgxTypography>
          <a id="agency-information-contact-support" href="mailto:support@autopilot.com.au">
            <AgxTypography
              id="agency-information-contact-support-label"
              type="hyperlink"
              size="medium"
            >
              Contact Autopilot Support
            </AgxTypography>
          </a>
        </AgxSpacer>
      </AgxSpacer>
    );
  }

  const canSkipOnboarding =
    formsLoadingState === LoadingState.Loaded &&
    !availableForms.some((form) => form.beginsCampaign);

  if (!agencyInformation.hasAccess && !canSkipOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
};

export default RequireIntegrationAccessLayout;
